import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import moment from 'moment';

import { useMyOrderPage } from '../../../../hooks/useMyOrderPage';
import InvoicedList from './components/InvoicedList';
import SubTotal from './components/SubTotal';
import { Accordion } from 'core/ui/atoms/V2';
import { Box, Button, Flex, Text, Hr } from 'core/ui/atoms';
import { NavigationHeader } from 'onramp/ui/components';
import { invoicesPath } from 'onramp/api/navigation';
import { GET_INVOICED_USER_SWAPS } from 'core/api/apollo/queries';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';

const ConfirmationPage = () => {
  const history = useHistory();
  const { invoiceId } = useParams();
  const { deliveryDays } = useMyOrderPage();

  const deliveryDayIds = deliveryDays.map((day) => day.id);

  const { loading, error, data: { getInvoicedUserSwaps } = {} } = useQuery(GET_INVOICED_USER_SWAPS, {
    variables: { deliveryDayIds: deliveryDayIds.toString(), invoiceId: invoiceId },
    skip: !invoiceId || deliveryDayIds.length === 0,
    fetchPolicy: 'no-cache',
  });

  if (isLoading(loading, getInvoicedUserSwaps, error)) {
    return <Loading />;
  }

  const calculateSubtotals = (orderItem) => {
    const addonSubtotal = orderItem.addons.reduce((sum, addon) =>
      sum + (addon.price.amount * addon.quantity), 0);

    const swapSubtotal = orderItem.swaps.reduce((sum, swap) =>
      sum + (swap.price.amount * swap.quantity), 0);

    return {
      totalSubtotal: {
        amount: addonSubtotal + swapSubtotal,
        __typename: 'Money',
      },
    };
  };

  const getWeekStartDate = (dayId) => {
    const deliveryDay = deliveryDays.find((day) => day.id === dayId);
    return deliveryDay ? moment(deliveryDay.date).format('MMM Do YYYY') : '';
  };

  const renderWeekItem = (order) => (
    <Box>
      <InvoicedList
        items={order.addons}
        type='addon'
      />
      <InvoicedList
        items={order.swaps}
        type='swap'
      />
      <SubTotal
        title='Sub Total'
        value={calculateSubtotals(order).totalSubtotal}
      />
    </Box>
  );

  const RenderWeeklyOrders = () => {
    return getInvoicedUserSwaps.map((order) => (
      <Accordion key={`order-${order.dayId}`}
        subMenus={[]}
        content={renderWeekItem(order)}>
        <AccordionTitle>{`Week of ${getWeekStartDate(order.dayId)}`}</AccordionTitle>
      </Accordion>
    ));
  };

  return (
    <Box>
      <NavigationHeader title='Order Confirmed' showNavigation={false}  />
      <Hr />
      <Message>Your order has been placed.</Message>
      <Message>If you need help with your order, email us at</Message>
      <Message>help@gomethodology.com or call us at +1 470 613 7332.</Message>
      <Container>
        <RenderWeeklyOrders />
      </Container>
      <FooterContainer direction='row' justifyContent='center' alignItems='center'>
        <ButtonUnderline btn-link onClick={() => history.push(invoicesPath())}>
          My Account
        </ButtonUnderline>
      </FooterContainer>
    </Box>
  );
};

const AccordionTitle = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0px;
  margin-top: 10px;
`;

const Container = styled(Box)`
  margin-top: 40px;
  padding-left: 30%;
  padding-right: 30%;
  ${(p) => p.theme.max('sm')`
    padding-left: 20px;
    padding-right: 20px;
  `}
`;

const FooterContainer = styled(Flex)`
  margin-top: 40px;
  padding-left: 20px;
  padding-right: 20px;
  margin-bottom: 80px;
`;

const ButtonUnderline = styled(Button)`
  text-decoration: underline;
  text-underline-offset: 8px;
`;

const Message = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding-left: 20px;
  padding-right: 20px;
  margin: 0px;
`;

export default ConfirmationPage;
